import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  addNoteComment,
  saveChfScreeningNotes,
  savePatientMRN,
  updateClinicNotes,
  updateClinicNotesWithQuickText,
  updateDiagnosisCodes,
  updateNoteComment,
  updateProviderNote,
  upsertPatientNotes,
  upsertReportNotes,
} from "app/services/transmission/notes";
import { BASE_URL } from "static/constants";
import { transmissionDetailApi } from "./transmissionDetailApi";

export const transmissionNotesApi = createApi({
  reducerPath: "transmissionNotesApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  endpoints: ({ mutation }) => ({
    saveClinicNotes: mutation({
      queryFn: ({
        reportId,
        clinicNotes,
        eSig,
        reportFIN,
        physicianNotes,
        finUpdate,
      }) => {
        return {
          data: updateClinicNotes(
            reportId,
            clinicNotes,
            eSig,
            reportFIN,
            physicianNotes,
            finUpdate
          ),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],

      async onQueryStarted({ eSig }, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        // A signing save moves on to the next report; refetching would flash the signed footer
        if (!eSig)
          dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    saveClinicMrn: mutation({
      queryFn: ({ patientId, patientMrn }) => {
        return {
          data: savePatientMRN(patientId, patientMrn),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    updatePatientNotes: mutation({
      queryFn: ({ serialNumber, noteList, reportId }) => {
        return {
          data: upsertPatientNotes(serialNumber, noteList, reportId),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],
    }),

    updateReportNotes: mutation({
      queryFn: ({
        reportId,
        billingDate,
        checkBillingDate,
        evokeNotes,
        autoScrollPageNumber,
        isAlertStatusUpdate,
        alertNotifications,
        skipAlertNotification,
        valid,
        displayPopup,
        nonBillable,
      }) => {
        return {
          data: upsertReportNotes(
            reportId,
            billingDate,
            checkBillingDate,
            evokeNotes,
            autoScrollPageNumber,
            isAlertStatusUpdate,
            alertNotifications,
            skipAlertNotification,
            valid,
            displayPopup,
            nonBillable
          ),
        };
      },

      async onQueryStarted({ reportId }, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(
          transmissionDetailApi.util.invalidateTags([
            "billingHistory",
            reportId,
          ])
        );
      },
    }),

    updateDiagnosisCodes: mutation({
      queryFn: ({ reportId, billingDiagnosisCodes }) => {
        return {
          data: updateDiagnosisCodes(reportId, billingDiagnosisCodes),
        };
      },

      async onQueryStarted({ reportId }, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(
          transmissionDetailApi.util.invalidateTags([
            "billingHistory",
            reportId,
          ])
        );
      },
    }),

    saveChfScreeningNotes: mutation({
      queryFn: (payload) => {
        return saveChfScreeningNotes(payload);
      },
    }),

    saveClinicNotesWithQuickText: mutation({
      queryFn: ({ reportId, clinicNotes }) => {
        return {
          data: updateClinicNotesWithQuickText(reportId, clinicNotes),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],
    }),
    saveProviderNote: mutation({
      queryFn: ({ reportId, notes }) => {
        return {
          data: updateProviderNote(reportId, notes),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],
    }),
    addNoteComment: mutation({
      queryFn: (payload) => {
        return {
          data: addNoteComment(payload),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],
    }),
    updateNoteComment: mutation({
      queryFn: (payload) => {
        return {
          data: updateNoteComment(payload),
        };
      },
      invalidatesTags: (reportId) => ["reportDetail", reportId],
    }),
  }),
});

export const {
  useSaveClinicNotesMutation,
  useSaveClinicMrnMutation,
  useUpdatePatientNotesMutation,
  useUpdateReportNotesMutation,
  useUpdateDiagnosisCodesMutation,
  useSaveChfScreeningNotesMutation,
  useSaveClinicNotesWithQuickTextMutation,
  useSaveProviderNoteMutation,
  useAddNoteCommentMutation,
  useUpdateNoteCommentMutation,
} = transmissionNotesApi;
